/* eslint-disable import/no-cycle */
/**
 * Per-language labels for the EMAIL countdown widget (`spm_widget_countdown`).
 *
 * Display templates render their unit labels as real text nodes, so the native
 * translation pipeline picks them up for free. E-mail clients can't run JS, so the
 * e-mail countdown is an animated GIF whose labels live INSIDE the image URL
 * (`labelDays=…&labelHours=…`). Translating that URL as a whole is what SC-3715
 * reports as broken, and it cannot work: the URL is rebuilt from the widget's
 * `data-label-*` attributes on every editor load, and `syncTranslationsFromDOM`
 * then propagates the rebuilt (single-language) URL to every language at save.
 *
 * So the labels — not the URL — are what gets translated. Each label is a native
 * ATTRIBUTE translation carried by the widget (`data-countdown-label-days`, …):
 *
 *  - the translation panel edits them like any other text (same field type family
 *    as the game widgets' `data-spm-text`);
 *  - `buildEmailCountdownGifUrl` derives the GIF URL from those attributes, which
 *    already hold the currently displayed language;
 *  - at save the native pipeline puts `{LANG_…}` tokens back into the attributes and
 *    {@link restoreCountdownTokensInHtml} mirrors them into the URL, so the backend
 *    resolves the labels per recipient language with its existing `replaceAll`
 *    (`templates.service.ts`) — no backend change.
 *
 * The `<img>` itself carries `spm_no_translate`: its `src` must never become a
 * translation again.
 */
import { Translation } from '@/types';
import { createTranslationIdentifier } from '@/components/template-builder/utils/translate';
import {
  addTranslationsToState,
  getTranslationByKeyAndLang,
  removeTranslation,
  replaceTranslationInState,
} from '@/composables/template-editor/TemplateEditor';
import { TRANSLATION_ATTRIBUTE } from '@/components/template-builder/utils/constants';

/** Field type of a countdown label translation (drives the native attribute path). */
export const COUNTDOWN_LABEL_FIELD_TYPE = 'countdown-label';

/** Class excluding the GIF `<img>` from the native translator. */
export const COUNTDOWN_NO_TRANSLATE_CLASS = 'spm_no_translate';

export interface CountdownLabelField {
  /** Query-string parameter carrying the label in the GIF URL. */
  param: string;
  /** Attribute holding the translated label (one value per displayed language). */
  attr: string;
  /** Legacy attribute written by the configuration panel (default language). */
  configAttr: string;
  /** Value used when the widget has no label yet. */
  fallback: string;
}

export const COUNTDOWN_LABEL_FIELDS: CountdownLabelField[] = [
  {
    param: 'labelDays', attr: 'data-countdown-label-days', configAttr: 'data-label-days', fallback: 'JOURS',
  },
  {
    param: 'labelHours', attr: 'data-countdown-label-hours', configAttr: 'data-label-hours', fallback: 'HEURES',
  },
  {
    param: 'labelMinutes', attr: 'data-countdown-label-minutes', configAttr: 'data-label-minutes', fallback: 'MIN',
  },
  {
    param: 'labelSeconds', attr: 'data-countdown-label-seconds', configAttr: 'data-label-seconds', fallback: 'SEC',
  },
];

/** Widget selector — the `<table>` holding both the config attributes and the GIF. */
export const COUNTDOWN_WIDGET_SELECTOR = 'table.spm_widget_countdown';

/** The GIF rendered inside an e-mail countdown widget. */
export const COUNTDOWN_GIF_SELECTOR = 'img.spm_countdown_gif';

/** True when the field type is a countdown label translation. */
export const isCountdownLabelFieldType = (type: string): boolean => type === COUNTDOWN_LABEL_FIELD_TYPE;

/** The label field matching a translation attribute name, if any. */
export const countdownFieldByAttribute = (attribute: string): CountdownLabelField | undefined => COUNTDOWN_LABEL_FIELDS
  .find((field) => field.attr === attribute);

/** The label field a configuration-panel attribute feeds, if any. */
export const countdownFieldByConfigAttribute = (attribute: string): CountdownLabelField | undefined => COUNTDOWN_LABEL_FIELDS
  .find((field) => field.configAttr === attribute);

/**
 * Languages a countdown widget must carry a label for. Passed in by the caller so
 * this module stays independent of the shop composable (and easy to test).
 */
export interface CountdownLangContext {
  /** Ids of every language of the shop. */
  langs: string[];
  /** Shop default language — the one the configuration panel edits. */
  defaultLang: string;
}

/** Marker attribute pairing a label attribute with its translation key. */
const markerAttribute = (field: CountdownLabelField): string => `${TRANSLATION_ATTRIBUTE}-${field.attr}`;

/** Translation key held by a widget for a given label, or '' when not registered yet. */
const translationKeyOf = (widget: HTMLElement, field: CountdownLabelField): string => (
  widget.getAttribute(markerAttribute(field)) ?? ''
).replace('LANG_', '');

/** Section the widget belongs to — translations are stored per section. */
const sectionIdOf = (widget: HTMLElement): number => {
  const section = widget.closest('[data-spmelementid]');
  return Number(section?.getAttribute('data-spmelementid') ?? 0);
};

/**
 * Labels carried by a legacy GIF URL, keyed by query parameter. Returns an empty
 * object when the URL is not a countdown GIF or can't be parsed.
 */
export const labelsFromGifUrl = (url: string): Record<string, string> => {
  const labels: Record<string, string> = {};
  if (!url || !/\/countdown\/gif/.test(url)) return labels;
  try {
    const parsed = new URL(url, window.location.origin);
    COUNTDOWN_LABEL_FIELDS.forEach((field) => {
      const value = parsed.searchParams.get(field.param);
      if (value !== null && value.trim() !== '') {
        labels[field.param] = value;
      }
    });
  } catch (e) {
    // Unparseable URL — the caller falls back to the configuration attributes.
  }
  return labels;
};

/**
 * Recover the labels a merchant already translated before SC-3715, when the whole
 * GIF URL was the translation. Returns `{ lang: { param: label } }` and drops the
 * obsolete `src` translation (its key disappears from the HTML anyway once the
 * image stops being translatable).
 */
const harvestLegacyLabels = (
  widget: HTMLElement,
  context: CountdownLangContext,
): Record<string, Record<string, string>> => {
  const harvested: Record<string, Record<string, string>> = {};
  const image = widget.querySelector(COUNTDOWN_GIF_SELECTOR) as HTMLElement | null;
  if (!image) return harvested;

  const legacyKey = (image.getAttribute(`${TRANSLATION_ATTRIBUTE}-src`) ?? '').replace('LANG_', '');
  if (!legacyKey) return harvested;

  context.langs.forEach((lang) => {
    const translation = getTranslationByKeyAndLang(legacyKey, lang);
    const labels = labelsFromGifUrl(translation?.value ?? '');
    if (Object.keys(labels).length) {
      harvested[lang] = labels;
    }
  });

  image.removeAttribute(`${TRANSLATION_ATTRIBUTE}-src`);
  removeTranslation(legacyKey);

  return harvested;
};

/**
 * Register the four label translations of a countdown widget (once per widget) and
 * expose each label in its own attribute so the native pipeline can translate it.
 *
 * Values are seeded, in order of preference, from the labels a merchant already
 * translated into the legacy GIF URL, then from the configuration panel's
 * `data-label-*`, then from the field default.
 */
export const ensureCountdownLabelTranslations = (widget: HTMLElement, context: CountdownLangContext): void => {
  const image = widget.querySelector(COUNTDOWN_GIF_SELECTOR) as HTMLElement | null;
  if (image) {
    image.classList.add(COUNTDOWN_NO_TRANSLATE_CLASS);
  }

  const alreadyRegistered = COUNTDOWN_LABEL_FIELDS.every((field) => translationKeyOf(widget, field));
  if (alreadyRegistered) return;

  const harvested = harvestLegacyLabels(widget, context);
  const { defaultLang } = context;
  const sectionId = sectionIdOf(widget);

  COUNTDOWN_LABEL_FIELDS.forEach((field) => {
    if (translationKeyOf(widget, field)) return;

    const configValue = widget.getAttribute(field.configAttr) || field.fallback;
    const key = createTranslationIdentifier(field.attr);

    const translations: Translation[] = context.langs.map((lang) => ({
      section: sectionId,
      language: lang,
      fieldType: COUNTDOWN_LABEL_FIELD_TYPE,
      key,
      value: harvested[lang]?.[field.param] ?? configValue,
      callback: (value: string) => value,
    }));
    addTranslationsToState(translations);

    widget.setAttribute(markerAttribute(field), `LANG_${key}`);
    widget.setAttribute(field.attr, harvested[defaultLang]?.[field.param] ?? configValue);
  });
};

/**
 * Labels to render, keyed by query parameter. They come from the translated
 * attributes — which already hold the language currently displayed in the editor —
 * and fall back to the configuration panel's value for a widget not yet registered.
 */
export const countdownLabelsOf = (widget: HTMLElement): Record<string, string> => COUNTDOWN_LABEL_FIELDS
  .reduce((labels: Record<string, string>, field) => ({
    ...labels,
    [field.param]: widget.getAttribute(field.attr)
      || widget.getAttribute(field.configAttr)
      || field.fallback,
  }), {});

/**
 * Propagate a configuration-panel edit (`data-label-*`, always the default language)
 * to the translated attribute and to the default-language translation. The native
 * `replaceTranslationInState` also carries the new value over to the languages still
 * holding the old one, so an untranslated widget keeps following the panel.
 */
export const syncCountdownLabelFromConfig = (
  widget: HTMLElement,
  configAttr: string,
  value: string,
  context: CountdownLangContext,
): void => {
  const field = countdownFieldByConfigAttribute(configAttr);
  if (!field) return;

  ensureCountdownLabelTranslations(widget, context);
  widget.setAttribute(field.attr, value);

  const key = translationKeyOf(widget, field);
  if (key) {
    replaceTranslationInState(key, context.defaultLang, value);
  }
};

/**
 * Unit labels travel inside the GIF URL's query string. A character meaningful
 * there (`& = ? #`) or one that would break the HTML attribute (`" ' < >`) makes
 * the URL ambiguous once the backend injects the raw text — it can no longer find
 * the parameter boundaries. Labels are short unit words, so stripping these is
 * lossless in practice. Applied at input time (config panel + translation panel).
 */
export const sanitizeCountdownLabel = (value: string): string => value.replace(/[&=?#"'<>]/g, '');

/**
 * Rewrite the `labelX` parameters of a GIF URL with the given values.
 * Tokens (`{LANG_…}`) are written verbatim: the backend replaces them before the
 * URL is ever requested, so they must not be percent-encoded here.
 */
export const applyLabelsToGifUrl = (url: string, labels: Record<string, string>): string => {
  let result = url;
  COUNTDOWN_LABEL_FIELDS.forEach((field) => {
    const label = labels[field.param];
    if (label === undefined) return;
    const pattern = new RegExp(`([?&]${field.param}=)[^&]*`);
    result = pattern.test(result)
      ? result.replace(pattern, `$1${label}`)
      : `${result}${result.includes('?') ? '&' : '?'}${field.param}=${label}`;
  });
  return result;
};

/**
 * Save-time step: mirror the `{LANG_…}` tokens the native pipeline just wrote into
 * the label attributes back into the GIF URL, so the backend resolves the labels per
 * recipient language. Runs on the serialized section, after `replaceTranslationsByKey`.
 */
export const restoreCountdownTokensInHtml = (html: string): string => {
  if (!html.includes('spm_widget_countdown')) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const widgets = Array.from(doc.querySelectorAll(COUNTDOWN_WIDGET_SELECTOR)) as HTMLElement[];
  if (!widgets.length) return html;

  let changed = false;
  widgets.forEach((widget) => {
    const image = widget.querySelector(COUNTDOWN_GIF_SELECTOR) as HTMLElement | null;
    const source = image?.getAttribute('src');
    if (!source) return;

    const labels = COUNTDOWN_LABEL_FIELDS.reduce((acc: Record<string, string>, field) => {
      const token = widget.getAttribute(field.attr);
      return token ? { ...acc, [field.param]: token } : acc;
    }, {});

    // Drop the editor's cache-busting parameter — it must never reach the sent e-mail.
    const cleaned = source.replace(/([?&])_t=\d+&?/, (match, separator) => (match.endsWith('&') ? separator : ''));

    if (image && (Object.keys(labels).length || cleaned !== source)) {
      image.setAttribute('src', applyLabelsToGifUrl(cleaned, labels));
      changed = true;
    }
  });

  return changed ? doc.body.innerHTML : html;
};
